import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import axios from 'axios';

// Import fallback translations (in case backend is unavailable)
import translationNO from './locales/no/translation.json';
import translationEN from './locales/en/translation.json';

// Dynamically determine API URL based on current domain
const getApiUrl = () => {
  const currentHost = window.location.hostname;
  const currentProtocol = window.location.protocol;
  
  const alternativeDomains = ['infovegg.displaysystem.no'];
  
  if (alternativeDomains.includes(currentHost)) {
    return `${currentProtocol}//${currentHost}/api`;
  }
  
  return `${process.env.REACT_APP_BACKEND_URL}/api`;
};

const API = getApiUrl();

// Initial resources with fallback translations
const resources = {
  no: { translation: translationNO },
  en: { translation: translationEN }
};

// Track which languages have been loaded from backend
const loadedLanguages = new Set(['no', 'en']); // Fallbacks already loaded
let availableLanguages = []; // Will be populated from backend

// Normalize language codes to match our supported languages
const normalizeLanguage = (lng) => {
  if (!lng) return 'no';
  const lngLower = lng.toLowerCase().split('-')[0];
  
  const languageMap = {
    'no': 'no', 'nb': 'no', 'nn': 'no',
    'en': 'en', 'sv': 'sv', 'fi': 'fi',
    'da': 'da', 'de': 'de', 'it': 'it',
    'es': 'es', 'pl': 'pl'
  };
  
  return languageMap[lngLower] || 'en';
};

// Initialize i18n
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'no',
    debug: false,
    load: 'languageOnly',
    interpolation: { escapeValue: false },
    react: {
      useSuspense: false,
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed'
    },
    detection: {
      order: ['localStorage'],
      caches: ['localStorage'],
      convertDetectedLanguage: normalizeLanguage
    }
  });

/**
 * Lazy load a single language from backend
 * Only loads if not already loaded
 */
export const loadLanguage = async (languageCode) => {
  if (loadedLanguages.has(languageCode)) {
    return true; // Already loaded
  }
  
  try {
    const response = await axios.get(`${API}/languages/${languageCode}`);
    const translations = response.data.translations;
    
    i18n.addResourceBundle(languageCode, 'translation', translations, true, true);
    loadedLanguages.add(languageCode);
    console.log(`✅ Lazy loaded language: ${languageCode}`);
    return true;
  } catch (error) {
    console.error(`❌ Failed to load language ${languageCode}:`, error);
    return false;
  }
};

/**
 * Fetch list of available languages from backend (metadata only, not translations)
 */
const fetchAvailableLanguages = async () => {
  try {
    const response = await axios.get(`${API}/languages`);
    availableLanguages = response.data;
    console.log(`🌍 ${availableLanguages.length} languages available from backend`);
    return availableLanguages;
  } catch (error) {
    console.error('⚠️ Failed to fetch language list:', error);
    return [];
  }
};

/**
 * Load only the current language from backend on init
 * Much faster than loading all languages
 */
const loadCurrentLanguageOnly = async () => {
  // First, fetch the list of available languages
  await fetchAvailableLanguages();
  
  // Then load only the current language
  const currentLang = normalizeLanguage(i18n.language);
  await loadLanguage(currentLang);
  
  console.log(`🚀 i18n initialized with current language: ${currentLang}`);
};

// Listen for language changes and lazy load as needed
i18n.on('languageChanged', async (lng) => {
  const normalizedLng = normalizeLanguage(lng);
  if (!loadedLanguages.has(normalizedLng)) {
    await loadLanguage(normalizedLng);
  }
});

// Initialize - only load current language
loadCurrentLanguageOnly();

/**
 * Get list of available languages (for language selector dropdowns)
 */
export const getAvailableLanguages = () => availableLanguages;

/**
 * Force reload all languages (useful after admin uploads new language)
 */
export const reloadLanguages = async () => {
  loadedLanguages.clear();
  loadedLanguages.add('no'); // Keep fallbacks
  loadedLanguages.add('en');
  
  await fetchAvailableLanguages();
  
  // Reload current language
  const currentLang = normalizeLanguage(i18n.language);
  await loadLanguage(currentLang);
  
  console.log('🔄 Languages reloaded');
};

export default i18n;
